package com.macrofocus.eflux.network

import com.macrofocus.eflux.network.line.Line
import com.macrofocus.eflux.network.logical.LogicalBranchId
import com.macrofocus.eflux.network.logical.LogicalBusId
import com.macrofocus.eflux.network.logical.logicalBranchIdOf
import com.macrofocus.eflux.network.logical.logicalBusIdOf
import com.macrofocus.eflux.network.physical.PhysicalNetwork
import com.macrofocus.eflux.network.substation.Substation
import org.molap.dataframe.DataFrame
import org.molap.dataframe.toDataFrame
import org.molap.datetime.DateTimeTz
import org.molap.network.AbstractNetwork
import kotlin.reflect.KClass

class SubstationNetwork(
    val time: DateTimeTz,
    busNetwork: PhysicalNetwork,
    val capabilities: Map<KClass<out NetworkCapability>, NetworkCapability>
) : AbstractNetwork<Substation, Line>() {
    val includeNonGraphicalFeatures = false

    val powerNetworkCapability = getCapability<PowerNetworkCapability>()!!
    val geometryNetworkCapability = getCapability<GeometryNetworkCapability>()!!
    val riskComponentsCapability = getCapability<RiskComponentsCapability>()!!
    val eodCapability = getCapability<EODNetworkCapability>()!!

    val substations = busNetwork.nodes
        .groupBy { logicalBusIdOf(it.id) }
        .filter { includeNonGraphicalFeatures || geometryNetworkCapability.substations.contains(it.key) }
        .mapValues {
            Substation(
                it.key,
                CombinedBuses(it.key, it.value),
                powerNetworkCapability.substationData(it.key),
                geometryNetworkCapability.substationData(it.key),
                eodCapability.substationData(it.key)
            )
        }.toMutableMap()

    val lines = busNetwork.links
        .filter { logicalBusIdOf(it.from.id) != logicalBusIdOf(it.to.id) }
        .filter {  includeNonGraphicalFeatures || (substations.contains(logicalBusIdOf(it.from.id)) && substations.contains(logicalBusIdOf(it.to.id))) }
        .groupBy { logicalBranchIdOf(logicalBusIdOf(it.from.id), logicalBusIdOf(it.to.id), it.record.ckt) }
        .filter { includeNonGraphicalFeatures || geometryNetworkCapability.lines.contains(it.key) }
        .mapValues {
            if(it.value.size == 0) {
                println("No record found for " + it.key)
            } else if(it.value.size > 1) {
                println("Multiple (" + it.value.size + ") branches detected for " + it.key)
            }
            Line(it.key, CombinedBranches(it.key.from, it.key.to, it.value.first()),
                powerNetworkCapability.lineData(it.key),
                geometryNetworkCapability.lineData(it.key),
                getCapability<RiskNetworkCapability>()!!.lineData(it.key),
                getCapability<WeatherNetworkCapability>()!!.lineData(it.key),
                )
        }.toMutableMap()
    //            .groupBy { UndirectedLink(logicalNodes[logicalBusIdOf(it.from.id)], logicalNodes[logicalBusIdOf(it.to.id)]) }
//            .mapValues { CombinedBranches(it.key.from!!,it.key.to!!, it.value) }

    init {
        for (logicalBusId in geometryNetworkCapability.substations) {
            if(!substations.containsKey(logicalBusId)) {
                Substation(
                    logicalBusId,
                    null,
                    powerNetworkCapability.substationData(logicalBusId),
                    geometryNetworkCapability.substationData(logicalBusId),
                    eodCapability.substationData(logicalBusId)
                )

            }
        }
        for (logicalBranchId in geometryNetworkCapability.lines) {
            if(!lines.containsKey(logicalBranchId)) {
                Line(
                    logicalBranchId,
                    null,
                    powerNetworkCapability.lineData(logicalBranchId),
                    geometryNetworkCapability.lineData(logicalBranchId),
                    getCapability<RiskNetworkCapability>()!!.lineData(logicalBranchId),
                    getCapability<WeatherNetworkCapability>()!!.lineData(logicalBranchId),
                )

            }
        }

        substations.forEach { addNode(it.value) }
        lines.forEach { addUndirectedEdge(substations[it.key.from]!!, substations[it.key.to]!!, it.value) }
    }

    override fun getFrom(link: Line): Substation = substations[link.from]!!

    override fun getTo(link: Line): Substation = substations[link.to]!!

    inline fun <reified T : NetworkCapability> getCapability(): T? {
        return capabilities[T::class] as? T
    }

    val substationsDataFrameLegacy: DataFrame<LogicalBusId, String, Any?> by lazy {
        substations.toDataFrame {
            "logicalBusId" to { logicalBusId }
            "name" to { name }
            "physicalGeometry" to { physicalGeometry }
            "logicalGeometry" to { logicalGeometry }
        }
    }

    val substationDataFrame: DataFrame<LogicalBusId, String, Any?> by lazy {
        substations.toDataFrame(Substation.mapDataFrameBuilder)
    }

    val lineDataFrame: DataFrame<LogicalBranchId, String, Any?> by lazy {
        lines.toDataFrame(Line.mapDataFrameBuilder)
    }
}